import { useMemo, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import i18n from '@/lib/i18n';
import type { Database } from '@/integrations/supabase/types';
import { logger } from '@/lib/logger';
import { extractAdvisorFields } from '@/lib/auth/advisorMetadata';
import { trackSupabaseError } from '@/lib/analytics/errorHandler';
import { trackEvent } from '@/lib/posthog';
import { advisorEvents } from '@/lib/analytics/events';

// Note: isAdvisor is determined by EITHER:
// 1. profile.is_advisor flag (set during advisor signup)
// 2. Having active client connections
// This ensures advisors aren't redirected to client dashboard while connections are loading

type AdvisorConnectionRow = Database['public']['Tables']['advisor_connections']['Row'];

export type AdvisorRole =
  | 'lead_attorney'
  | 'associate_attorney'
  | 'paralegal'
  | 'cpa'
  | 'financial_advisor'
  | 'mediator'
  | 'expert_witness'
  | 'family_member';

export type ConnectionStatus = 'pending' | 'active' | 'revoked' | 'expired';

export interface AdvisorConnection {
  id: string;
  clientUserId: string;
  advisorUserId: string | null;
  advisorEmail: string;
  advisorName: string | null;
  role: AdvisorRole;
  status: ConnectionStatus;
  firmName: string | null;
  professionalLicense: string | null;
  notes: string | null;
  invitedAt: string;
  /** When the invitation link stops working. Null on older rows. (#1874) */
  invitationExpiresAt: string | null;
  acceptedAt: string | null;
  revokedAt: string | null;
  accessExpiresAt: string | null;
}

export const ROLE_LABELS: Record<AdvisorRole, { label: string; description: string }> = {
  lead_attorney: {
    label: 'Lead Attorney',
    description: 'Full case access, can view strategy and invite other advisors',
  },
  associate_attorney: {
    label: 'Associate Attorney',
    description: 'Case access with limited administrative permissions',
  },
  paralegal: {
    label: 'Paralegal',
    description: 'View and upload documents, no strategy access',
  },
  cpa: {
    label: 'CPA / Tax Advisor',
    description: 'Full financial visibility, can comment on tax implications',
  },
  financial_advisor: {
    label: 'Financial Advisor',
    description: 'Investment and retirement account focus',
  },
  mediator: {
    label: 'Mediator',
    description: 'Neutral view of agreed-upon data',
  },
  expert_witness: {
    label: 'Expert Witness',
    description: 'Limited view for testimony preparation',
  },
  family_member: {
    label: 'Family Member',
    description: 'Very limited access for emotional support',
  },
};

function mapConnectionFromDb(row: AdvisorConnectionRow): AdvisorConnection {
  return {
    id: row.id,
    clientUserId: row.client_user_id,
    advisorUserId: row.advisor_user_id,
    advisorEmail: row.advisor_email,
    advisorName: row.advisor_name,
    role: row.role as AdvisorRole,
    status: row.status as ConnectionStatus,
    firmName: row.firm_name,
    professionalLicense: row.professional_license,
    notes: row.notes,
    invitedAt: row.invited_at || row.created_at,
    invitationExpiresAt: row.invitation_expires_at ?? null,
    acceptedAt: row.accepted_at,
    revokedAt: row.revoked_at,
    accessExpiresAt: row.access_expires_at,
  };
}

interface InviteAdvisorParams {
  advisorEmail: string;
  advisorName: string;
  role: AdvisorRole;
  firmName?: string;
  professionalLicense?: string;
  notes?: string;
}

interface AcceptInvitationParams {
  token: string;
}

export function useAdvisorConnections() {
  const { user, profile } = useAuth();
  const queryClient = useQueryClient();

  // Fetch all connections where current user is the client
  const {
    data: connections = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['advisor-connections', user?.id],
    queryFn: async (): Promise<AdvisorConnection[]> => {
      if (!user) return [];

      const { data, error } = await (supabase as any)
        .from('advisor_connections')
        .select('*')
        .eq('client_user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        trackSupabaseError('select', 'advisor_connections', error);
        throw error;
      }
      return (data || []).map(mapConnectionFromDb);
    },
    enabled: !!user,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

  // Fetch connections where current user is an advisor (for advisor dashboard)
  // This also handles team members who should see their parent advisor's clients
  const {
    data: clientConnections = [],
    isLoading: isLoadingClients,
  } = useQuery({
    queryKey: ['advisor-client-connections', user?.id],
    queryFn: async (): Promise<AdvisorConnection[]> => {
      if (!user) return [];

      // First, check if current user is a primary advisor (has their own advisor_profiles record)
      let { data: ownProfile } = await (supabase as any)
        .from('advisor_profiles')
        .select('user_id')
        .eq('user_id', user.id)
        .maybeSingle();

      // Phase 3.2: Auto-create missing advisor_profiles if profile.is_advisor is true
      // This fixes cases where the advisor_profiles record was not created during signup
      if (!ownProfile && profile?.is_advisor) {
        logger.warn('[useAdvisorConnections] Creating missing advisor_profiles record for advisor user');
        const advisorFields = extractAdvisorFields(user.user_metadata);
        const { data: newProfile, error: createError } = await (supabase as any)
          .from('advisor_profiles')
          .insert({
            user_id: user.id,
            full_name: advisorFields.fullName || profile.full_name || '',
            profession_type: advisorFields.professionType,
            firm_name: advisorFields.firmName,
            license_number: advisorFields.licenseNumber,
            license_state: advisorFields.licenseState,
            verification_status: 'self_attested',
          })
          .select('user_id')
          .single();

        if (createError) {
          logger.error('[useAdvisorConnections] Failed to create advisor_profiles:', createError);
        } else {
          logger.log('[useAdvisorConnections] Created missing advisor_profiles record');
          ownProfile = newProfile;
        }
      }

      let advisorUserId = user.id;

      // If not a primary advisor, check if they're a team member
      if (!ownProfile) {
        const { data: teamMembership } = await (supabase as any)
          .from('advisor_team_members')
          .select('advisor_id, advisor_profiles(user_id)')
          .eq('member_user_id', user.id)
          .eq('status', 'active')
          .maybeSingle();

        if (teamMembership) {
          // Get the parent advisor's user_id from the joined data
          const parentProfile = teamMembership.advisor_profiles as unknown as { user_id: string } | null;
          if (parentProfile?.user_id) {
            advisorUserId = parentProfile.user_id;
          }
        }
      }

      // Fetch connections for the determined advisor user_id
      const { data, error } = await (supabase as any)
        .from('advisor_connections')
        .select('*')
        .eq('advisor_user_id', advisorUserId)
        .eq('status', 'active')
        .order('accepted_at', { ascending: false });

      if (error) {
        trackSupabaseError('select', 'advisor_connections', error);
        throw error;
      }
      return (data || []).map(mapConnectionFromDb);
    },
    enabled: !!user,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

  // Invite a new advisor
  const inviteMutation = useMutation({
    mutationFn: async (params: InviteAdvisorParams) => {
      const { data: { session } } = await (supabase as any).auth.getSession();
      if (!session) throw new Error(i18n.t('hooks.common.notAuthenticated'));

      const response = await (supabase as any).functions.invoke('invite-advisor', {
        body: params,
      });

      if (response.error) {
        throw new Error(response.error.message || i18n.t('hooks.advisorConnections.inviteFailed'));
      }

      if (response.data?.error) {
        throw new Error(response.data.error);
      }

      return response.data;
    },
    onSuccess: (_, params) => {
      queryClient.invalidateQueries({ queryKey: ['advisor-connections'] });
      const evt = advisorEvents.inviteSent(params.role, 'dialog');
      trackEvent(evt.event, evt.properties);
      trackEvent('feature_activated', { feature: 'advisor_collaboration' });
    },
  });

  // Accept an invitation (for advisors)
  const acceptMutation = useMutation({
    mutationFn: async (params: AcceptInvitationParams) => {
      const response = await (supabase as any).functions.invoke('accept-advisor-invitation', {
        body: params,
      });

      logger.log('[useAdvisorConnections] Accept response:', {
        hasData: !!response.data,
        hasError: !!response.error,
        dataKeys: response.data ? Object.keys(response.data) : [],
        errorMessage: response.error?.message,
      });

      // Handle errors from edge function
      // When supabase.functions.invoke gets a non-2xx response:
      // - response.error is set with message like "Edge Function returned a non-2xx status code"
      // - response.error.context is the raw Response object (needs .json() parsing)
      // - response.data is typically null
      if (response.error) {
        let errBody: Record<string, unknown> = {};

        // The error.context is a Response object - we need to parse it
        const ctx = (response.error as unknown as { context?: unknown }).context;
        if (ctx instanceof Response) {
          try {
            // Clone the response since it may have already been read
            const clonedResponse = ctx.clone();
            errBody = await clonedResponse.json();
            logger.log('[useAdvisorConnections] Parsed error body from Response:', errBody);
          } catch (parseError) {
            logger.error('[useAdvisorConnections] Failed to parse error response:', parseError);
          }
        } else if (ctx && typeof ctx === 'object') {
          // Some versions may have pre-parsed JSON in context
          errBody = ctx as Record<string, unknown>;
        } else if (response.data && typeof response.data === 'object' && 'error' in response.data) {
          // Fallback: check if error is in response.data
          errBody = response.data as Record<string, unknown>;
        }

        logger.log('[useAdvisorConnections] Final error body:', errBody);

        const err = new Error(
          (errBody.message as string) || (errBody.error as string) || response.error.message || i18n.t('hooks.advisorConnections.acceptFailed')
        ) as Error & {
          requiresMFA?: boolean;
          requiresVerification?: boolean;
          expectedEmail?: string;
        };
        // CRITICAL: Preserve the structured fields from edge function response!
        err.requiresMFA = errBody.requiresMFA as boolean | undefined;
        err.requiresVerification = errBody.requiresVerification as boolean | undefined;
        err.expectedEmail = errBody.expectedEmail as string | undefined;

        logger.log('[useAdvisorConnections] Throwing error with fields:', {
          message: err.message,
          requiresMFA: err.requiresMFA,
          requiresVerification: err.requiresVerification,
          expectedEmail: err.expectedEmail,
        });

        throw err;
      }

      // Handle case where function returns 200 but with error in body
      if (response.data?.error) {
        throw new Error(response.data.message || response.data.error);
      }

      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['advisor-client-connections'] });
      const evt = advisorEvents.inviteAccepted(data?.role ?? 'unknown');
      trackEvent(evt.event, evt.properties);
    },
  });

  // Revoke advisor access
  const revokeMutation = useMutation({
    mutationFn: async (connectionId: string) => {
      if (!user) throw new Error(i18n.t('hooks.common.notAuthenticated'));

      const { error } = await (supabase as any)
        .from('advisor_connections')
        .update({
          status: 'revoked',
          revoked_at: new Date().toISOString(),
          revoked_by: user.id,
        })
        .eq('id', connectionId)
        .eq('client_user_id', user.id); // Ensure only the client can revoke

      if (error) {
        trackSupabaseError('update', 'advisor_connections', error);
        throw error;
      }

      // Log audit entry
      await (supabase as any).from('advisor_audit_log').insert({
        client_user_id: user.id,
        actor_user_id: user.id,
        actor_role: 'owner',
        action: 'revoke',
        resource_type: 'advisor',
        resource_id: connectionId,
      });
    },
    onSuccess: (_, connectionId) => {
      queryClient.invalidateQueries({ queryKey: ['advisor-connections'] });
      const connection = connections.find(c => c.id === connectionId);
      const evt = advisorEvents.accessRevoked(connection?.role ?? 'unknown');
      trackEvent(evt.event, evt.properties);
    },
  });

  // Resend invitation
  const resendMutation = useMutation({
    mutationFn: async (connectionId: string) => {
      const connection = connections.find(c => c.id === connectionId);
      if (!connection) throw new Error(i18n.t('hooks.advisorConnections.connectionNotFound'));
      if (connection.status !== 'pending') throw new Error(i18n.t('hooks.advisorConnections.onlyResendPending'));

      // Delete old connection and create new one with fresh token
      const { error: deleteError } = await (supabase as any)
        .from('advisor_connections')
        .delete()
        .eq('id', connectionId);

      if (deleteError) {
        logger.error('Failed to delete old invitation:', deleteError);
        throw new Error(i18n.t('hooks.advisorConnections.deleteOldInvitationFailed'));
      }

      // Re-invite with same details
      const result = await inviteMutation.mutateAsync({
        advisorEmail: connection.advisorEmail,
        advisorName: connection.advisorName || '',
        role: connection.role,
        firmName: connection.firmName || undefined,
        professionalLicense: connection.professionalLicense || undefined,
        notes: connection.notes || undefined,
      });

      // Check for warning (email may have failed)
      if (result?.warning) {
        logger.warn('Invitation warning:', result.warning);
      }

      return { ...result, _role: connection.role };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['advisor-connections'] });
      const evt = advisorEvents.inviteResent(data?._role ?? 'unknown');
      trackEvent(evt.event, evt.properties);
    },
  });

  // Memoize computed values to prevent unnecessary re-renders
  const activeConnections = useMemo(
    () => connections.filter(c => c.status === 'active'),
    [connections]
  );
  const pendingConnections = useMemo(
    () => connections.filter(c => c.status === 'pending'),
    [connections]
  );
  const revokedConnections = useMemo(
    () => connections.filter(c => c.status === 'revoked'),
    [connections]
  );

  // User is an advisor if either:
  // 1. Their profile has is_advisor flag set (from signup)
  // 2. They have active client connections
  // This prevents race conditions where profile loads before connections
  const isAdvisor = useMemo(
    () => profile?.is_advisor === true || clientConnections.length > 0,
    [profile?.is_advisor, clientConnections.length]
  );

  // Stable references for mutation functions
  const inviteAdvisor = useCallback(
    (params: InviteAdvisorParams) => inviteMutation.mutateAsync(params),
    [inviteMutation.mutateAsync]
  );
  const acceptInvitation = useCallback(
    (params: AcceptInvitationParams) => acceptMutation.mutateAsync(params),
    [acceptMutation.mutateAsync]
  );
  const revokeAccess = useCallback(
    (connectionId: string) => revokeMutation.mutateAsync(connectionId),
    [revokeMutation.mutateAsync]
  );
  const resendInvitation = useCallback(
    (connectionId: string) => resendMutation.mutateAsync(connectionId),
    [resendMutation.mutateAsync]
  );

  // Memoize the entire return object to prevent unnecessary re-renders
  return useMemo(() => ({
    // As client
    connections,
    activeConnections,
    pendingConnections,
    revokedConnections,
    isLoading,
    error,
    refetch,

    // As advisor
    clientConnections,
    isLoadingClients,
    isAdvisor,

    // Actions
    inviteAdvisor,
    isInviting: inviteMutation.isPending,
    inviteError: inviteMutation.error,

    acceptInvitation,
    isAccepting: acceptMutation.isPending,
    acceptError: acceptMutation.error,

    revokeAccess,
    isRevoking: revokeMutation.isPending,
    revokeError: revokeMutation.error,

    resendInvitation,
    isResending: resendMutation.isPending,
  }), [
    connections,
    activeConnections,
    pendingConnections,
    revokedConnections,
    isLoading,
    error,
    refetch,
    clientConnections,
    isLoadingClients,
    isAdvisor,
    inviteAdvisor,
    inviteMutation.isPending,
    inviteMutation.error,
    acceptInvitation,
    acceptMutation.isPending,
    acceptMutation.error,
    revokeAccess,
    revokeMutation.isPending,
    revokeMutation.error,
    resendInvitation,
    resendMutation.isPending,
  ]);
}
